const STORAGE_KEY = "checkout_tracking_logs";

// Benzersiz session ID oluştur
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Mevcut tracking session'ı
let currentSession = null;

/**
 * Yeni bir checkout session başlat
 */
export const startCheckoutSession = (cartItems = [], userId = null) => {
  currentSession = {
    sessionId: generateSessionId(),
    userId: userId || "guest",
    startTime: new Date().toISOString(),
    endTime: null,
    cartItemCount: cartItems.length, // Sadece ürün sayısı
    formFields: {
      name: { filled: false, value: "", timestamp: null },
      email: { filled: false, value: "", timestamp: null, verified: false },
      phone: { filled: false, value: "", timestamp: null },
      address1: { filled: false, value: "", timestamp: null },
      address2: { filled: false, value: "", timestamp: null },
      city: { filled: false, value: "", timestamp: null },
      state: { filled: false, value: "", timestamp: null },
      country: { filled: false, value: "", timestamp: null },
      creditCardName: { filled: false, value: "", timestamp: null },
      creditCardNumber: { filled: false, value: "", timestamp: null }, // Son 4 hane
      expirationDate: { filled: false, value: "", timestamp: null },
      securityCode: { filled: false, value: "***", timestamp: null }, // Güvenlik için gizli
    },
    paymentMethod: null,
    paymentAttempts: [],
    validationErrors: [],
    events: [],
    status: "in_progress", // in_progress, completed, abandoned, failed
    abandonReason: null,
    userAgent: navigator.userAgent,
    language: navigator.language,
    currency: null,
  };

  // Başlangıç event'i ekle
  addEvent("session_started", { cartItemCount: cartItems.length });

  // Session başladığında hemen kaydet!
  saveSession();

  return currentSession;
};

/**
 * Form alanı değişikliğini kaydet (değer ile birlikte)
 */
export const trackFieldChange = (fieldName, value) => {
  if (!currentSession) return;

  const field = currentSession.formFields[fieldName];
  if (field) {
    const hasValue = value && String(value).trim().length > 0;
    const wasFilled = field.filled;
    field.filled = hasValue;
    field.timestamp = new Date().toISOString();

    // Değeri kaydet (kart numarası için sadece son 4 hane, CVV gizli)
    if (fieldName === "creditCardNumber") {
      const digits = String(value || "").replace(/\D/g, "");
      field.value = digits.length >= 4 ? "****" + digits.slice(-4) : "";
    } else if (fieldName === "securityCode") {
      field.value = hasValue ? "***" : "";
    } else {
      field.value = String(value || "").trim();
    }

    // Sadece ilk doldurmada veya boşaltmada event ekle
    if (wasFilled !== hasValue) {
      addEvent("field_change", {
        field: fieldName,
        action: hasValue ? "filled" : "cleared",
        value: field.value,
      });
    }

    // Her değişiklikte hemen kaydet!
    saveSession();
  }
};

/**
 * Toplu form durumu güncelleme
 */
export const trackFormState = (formData) => {
  if (!currentSession) return;

  Object.keys(formData).forEach((key) => {
    if (currentSession.formFields[key] !== undefined) {
      const hasValue = formData[key] && String(formData[key]).trim().length > 0;
      trackFieldChange(key, hasValue);
    }
  });
};

/**
 * Ödeme denemesini kaydet
 */
export const trackPaymentAttempt = (paymentMethod, paymentData = {}) => {
  if (!currentSession) return;

  const attempt = {
    attemptNumber: currentSession.paymentAttempts.length + 1,
    timestamp: new Date().toISOString(),
    paymentMethod,
    status: "pending",
    errorCode: null,
    errorMessage: null,
    responseData: null,
    paymentData: paymentData, // Ek bilgileri de kaydet
  };

  currentSession.paymentMethod = paymentMethod;
  currentSession.paymentAttempts.push(attempt);

  addEvent("payment_attempt", {
    attemptNumber: attempt.attemptNumber,
    method: paymentMethod,
    data: paymentData,
  });

  // Hemen kaydet!
  saveSession();

  return attempt.attemptNumber;
};

/**
 * Ödeme sonucunu kaydet
 */
export const trackPaymentResult = (
  attemptNumber,
  success,
  errorCode = null,
  errorMessage = null,
  responseData = null
) => {
  if (!currentSession) return;

  const attempt = currentSession.paymentAttempts.find(
    (a) => a.attemptNumber === attemptNumber
  );
  if (attempt) {
    attempt.status = success ? "success" : "failed";
    attempt.errorCode = errorCode;
    attempt.errorMessage = errorMessage;
    attempt.responseData = responseData;
    attempt.completedAt = new Date().toISOString();
  }

  addEvent("payment_result", {
    attemptNumber,
    success,
    errorCode,
    errorMessage,
  });

  if (success) {
    currentSession.status = "completed";
    currentSession.endTime = new Date().toISOString();
    saveSession();
  } else {
    currentSession.status = "failed";
    saveSession();
  }
};

/**
 * Validasyon hatasını kaydet
 */
export const trackValidationError = (field, errorType, errorMessage) => {
  if (!currentSession) return;

  const error = {
    timestamp: new Date().toISOString(),
    field,
    errorType,
    errorMessage,
  };

  currentSession.validationErrors.push(error);

  addEvent("validation_error", { field, errorType, errorMessage });

  // Hemen kaydet!
  saveSession();
};

/**
 * Sayfa terk etmeyi kaydet (beforeunload)
 */
export const trackAbandon = (reason = "page_leave") => {
  if (!currentSession || currentSession.status === "completed") return;

  currentSession.status = "abandoned";
  currentSession.abandonReason = reason;
  currentSession.endTime = new Date().toISOString();

  addEvent("abandoned", { reason });

  // Hemen kaydet (sayfa kapanmadan önce)
  saveSession();
};

/**
 * Genel event ekle
 */
const addEvent = (eventType, data = {}) => {
  if (!currentSession) return;

  currentSession.events.push({
    timestamp: new Date().toISOString(),
    type: eventType,
    data,
  });
};

/**
 * Para birimi ayarla
 */
export const setCurrency = (currency) => {
  if (!currentSession) return;
  currentSession.currency = currency;
  saveSession();
};

/**
 * Email doğrulama durumunu güncelle
 */
export const trackEmailVerified = (verified) => {
  if (!currentSession) return;
  currentSession.formFields.email.verified = verified;
  addEvent("email_verification", { verified });
  saveSession();
};

/**
 * Session'ı kaydet (sadece localStorage)
 */
const saveSession = () => {
  if (!currentSession) return;

  // Tamamlanmamış oturumları hesapla
  const filledFields = Object.values(currentSession.formFields).filter(
    (f) => f.filled
  ).length;
  const totalFields = Object.keys(currentSession.formFields).length;
  currentSession.formCompletionPercentage = Math.round(
    (filledFields / totalFields) * 100
  );

  // Duration hesapla
  const start = new Date(currentSession.startTime);
  const end = currentSession.endTime
    ? new Date(currentSession.endTime)
    : new Date();
  currentSession.durationSeconds = Math.round((end - start) / 1000);

  // LocalStorage'a kaydet
  saveToLocalStorage();
};

/**
 * LocalStorage'a kaydet
 */
const saveToLocalStorage = () => {
  try {
    const existingLogs = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

    // Mevcut session'ı güncelle veya ekle
    const existingIndex = existingLogs.findIndex(
      (log) => log.sessionId === currentSession.sessionId
    );
    if (existingIndex >= 0) {
      existingLogs[existingIndex] = currentSession;
    } else {
      existingLogs.push(currentSession);
    }

    // Son 100 kaydı tut (eski kayıtları temizle)
    const trimmedLogs = existingLogs.slice(-100);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (error) {
    console.error("Checkout tracking localStorage error:", error);
  }
};

/**
 * LocalStorage'daki tüm logları getir
 */
export const getAllLogs = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

/**
 * Logları JSON olarak export et
 */
export const exportLogsAsJSON = () => {
  const logs = getAllLogs();
  const dataStr = JSON.stringify(logs, null, 2);
  const blob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `checkout_logs_${
    new Date().toISOString().split("T")[0]
  }.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Logları CSV olarak export et (tüm input değerleri dahil)
 */
export const exportLogsAsCSV = () => {
  const logs = getAllLogs();

  const headers = [
    "Session ID",
    "User ID",
    "Start Time",
    "End Time",
    "Duration (s)",
    "Status",
    "Abandon Reason",
    "Form Completion %",
    "Payment Method",
    "Payment Attempts",
    "Validation Errors",
    "Currency",
    // Form alanları
    "Name",
    "Email",
    "Email Verified",
    "Phone",
    "Address1",
    "Address2",
    "City",
    "State",
    "Country",
    "Card Holder Name",
    "Card Number (Last 4)",
    "Expiration Date",
  ];

  const rows = logs.map((log) => {
    const f = log.formFields || {};
    return [
      log.sessionId,
      log.userId,
      log.startTime,
      log.endTime || "",
      log.durationSeconds || "",
      log.status,
      log.abandonReason || "",
      log.formCompletionPercentage || 0,
      log.paymentMethod || "",
      log.paymentAttempts?.length || 0,
      log.validationErrors?.length || 0,
      log.currency || "",
      // Form değerleri
      f.name?.value || "",
      f.email?.value || "",
      f.email?.verified ? "Yes" : "No",
      f.phone?.value || "",
      f.address1?.value || "",
      f.address2?.value || "",
      f.city?.value || "",
      f.state?.value || "",
      f.country?.value || "",
      f.creditCardName?.value || "",
      f.creditCardNumber?.value || "",
      f.expirationDate?.value || "",
    ];
  });

  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `checkout_logs_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Logları temizle
 */
export const clearLogs = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Özet istatistikler
 */
export const getLogsSummary = () => {
  const logs = getAllLogs();

  const summary = {
    totalSessions: logs.length,
    completed: logs.filter((l) => l.status === "completed").length,
    abandoned: logs.filter((l) => l.status === "abandoned").length,
    failed: logs.filter((l) => l.status === "failed").length,
    averageFormCompletion: 0,
    averageDuration: 0,
    commonErrors: {},
    abandonReasons: {},
  };

  // Ortalamalar
  const completionRates = logs.map((l) => l.formCompletionPercentage || 0);
  const durations = logs
    .map((l) => l.durationSeconds || 0)
    .filter((d) => d > 0);

  if (completionRates.length > 0) {
    summary.averageFormCompletion = Math.round(
      completionRates.reduce((a, b) => a + b, 0) / completionRates.length
    );
  }

  if (durations.length > 0) {
    summary.averageDuration = Math.round(
      durations.reduce((a, b) => a + b, 0) / durations.length
    );
  }

  // Yaygın hatalar
  logs.forEach((log) => {
    (log.validationErrors || []).forEach((err) => {
      const key = `${err.field}: ${err.errorType}`;
      summary.commonErrors[key] = (summary.commonErrors[key] || 0) + 1;
    });

    if (log.abandonReason) {
      summary.abandonReasons[log.abandonReason] =
        (summary.abandonReasons[log.abandonReason] || 0) + 1;
    }
  });

  return summary;
};

/**
 * Mevcut session'ı getir
 */
export const getCurrentSession = () => currentSession;

const checkoutTracker = {
  startCheckoutSession,
  trackFieldChange,
  trackFormState,
  trackPaymentAttempt,
  trackPaymentResult,
  trackValidationError,
  trackAbandon,
  trackEmailVerified,
  setCurrency,
  getAllLogs,
  exportLogsAsJSON,
  exportLogsAsCSV,
  clearLogs,
  getLogsSummary,
  getCurrentSession,
};

export default checkoutTracker;
